import React, { useCallback, useEffect, useState } from 'react';
import { Box } from '@chakra-ui/react';
import { createPortal } from 'react-dom';
import { useAuthenticatedUserInfo } from './AuthenticatedUserInfoContext';
import { useIdleSession } from '../../hooks/useIdleSession';
import { useSessionLockTamperGuard } from '../../hooks/useSessionLockTamperGuard';
import SessionLockOverlay from '../../components/SessionLock/SessionLockOverlay';

const SessionIdleProvider = ({ children }) => {
	const {
		token,
		userId,
		loading,
		logout,
		userEmail,
		profileImage,
		user,
	} = useAuthenticatedUserInfo();

	const isAuthenticated = Boolean(token && userId && !loading);
	const { isLocked, unlock, reinforceLock } = useIdleSession({
		enabled: isAuthenticated,
		onLogout: logout,
	});

	const [overlayKey, setOverlayKey] = useState(0);

	const handleReinforceLock = useCallback(() => {
		reinforceLock();
		setOverlayKey((key) => key + 1);
	}, [reinforceLock]);

	useSessionLockTamperGuard({
		active: isAuthenticated && isLocked,
		onReinforceLock: handleReinforceLock,
		onLogout: logout,
	});

	useEffect(() => {
		if (!isAuthenticated || !isLocked) return undefined;

		const { body, documentElement: html } = document;
		const scrollY = window.scrollY;

		const previousBodyOverflow = body.style.overflow;
		const previousHtmlOverflow = html.style.overflow;
		const previousBodyPosition = body.style.position;
		const previousBodyTop = body.style.top;
		const previousBodyWidth = body.style.width;
		const scrollbarWidth = window.innerWidth - html.clientWidth;

		body.style.overflow = 'hidden';
		html.style.overflow = 'hidden';
		body.style.position = 'fixed';
		body.style.top = `-${scrollY}px`;
		body.style.width = '100%';

		if (scrollbarWidth > 0) {
			body.style.paddingRight = `${scrollbarWidth}px`;
		}

		return () => {
			body.style.overflow = previousBodyOverflow;
			html.style.overflow = previousHtmlOverflow;
			body.style.position = previousBodyPosition;
			body.style.top = previousBodyTop;
			body.style.width = previousBodyWidth;
			body.style.paddingRight = '';

			window.scrollTo(0, scrollY);
		};
	}, [isAuthenticated, isLocked]);

	const userObj = user?.data || null;
	const displayName = userObj
		? `${userObj.firstName || ''} ${userObj.lastName || ''}`.trim()
		: '';
	const profileImageSrc = profileImage || userObj?.profileImage || null;

	const lockUi =
		isAuthenticated && isLocked ? (
			<>
				<Box
					data-session-lock-shield
					position='fixed'
					inset={0}
					zIndex={9998}
					bg='transparent'
					pointerEvents='auto'
				/>
				<SessionLockOverlay
					key={overlayKey}
					displayName={displayName}
					email={userEmail}
					profileImageSrc={profileImageSrc}
					onUnlock={unlock}
					onSignOut={logout}
				/>
			</>
		) : null;

	return (
		<>
			<Box
				position='relative'
				flex='1'
				minH='0'
				overflow={isLocked ? 'hidden' : 'visible'}
				pointerEvents={isLocked ? 'none' : 'auto'}
				userSelect={isLocked ? 'none' : 'auto'}
				aria-hidden={isLocked || undefined}>
				{children}
			</Box>
			{typeof document !== 'undefined'
				? createPortal(lockUi, document.body)
				: lockUi}
		</>
	);
};

export default SessionIdleProvider;
